/**
 * Query Keys padronizadas para React Query
 *
 * Estrutura: [recurso, identificador/filtros, variante?]
 *
 * Isso permite invalidação granular:
 * - queryClient.invalidateQueries({ queryKey: ['user-investments'] }) - invalida todas
 * - queryClient.invalidateQueries({ queryKey: ['user-investments', userId] }) - invalida de um usuário
 */

import type { MyInvestmentsFilters } from '@/types/investments';

// ========================================
// USER INVESTMENTS (Carteira do Investidor)
// ========================================
export const userInvestmentsKeys = {
  all: ['user-investments'] as const,
  lists: () => [...userInvestmentsKeys.all, 'list'] as const,
  list: (userId: string, filters?: MyInvestmentsFilters) =>
    [...userInvestmentsKeys.lists(), userId, filters] as const,
  details: () => [...userInvestmentsKeys.all, 'detail'] as const,
  detail: (userInvestmentId: string) =>
    [...userInvestmentsKeys.details(), userInvestmentId] as const,
};

// ========================================
// INVESTMENTS (Produtos de Investimento)
// ========================================
export const investmentsKeys = {
  all: ['investments'] as const,
  lists: () => [...investmentsKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...investmentsKeys.lists(), filters] as const,
  details: () => [...investmentsKeys.all, 'detail'] as const,
  detail: (investmentId: string) =>
    [...investmentsKeys.details(), investmentId] as const,
  // Sub-queries de um investimento específico
  documents: (investmentId: string) =>
    [...investmentsKeys.detail(investmentId), 'documents'] as const,
  aportes: (investmentId: string) =>
    [...investmentsKeys.detail(investmentId), 'aportes'] as const,
  performance: (investmentId: string) =>
    [...investmentsKeys.detail(investmentId), 'performance'] as const,
};

// ========================================
// DASHBOARD METRICS
// ========================================
export const dashboardKeys = {
  all: ['dashboard'] as const,
  investor: (userId: string) => [...dashboardKeys.all, 'investor', userId] as const,
  admin: () => [...dashboardKeys.all, 'admin'] as const,
  partner: (partnerId: string) => [...dashboardKeys.all, 'partner', partnerId] as const,
  assessor: (assessorId: string) => [...dashboardKeys.all, 'assessor', assessorId] as const,
  mfb: (userId: string) => [...dashboardKeys.all, 'mfb', userId] as const,
  mfa: (userId: string) => [...dashboardKeys.all, 'mfa', userId] as const,
};

// ========================================
// EXTRATOS (Movimentações)
// ========================================
export const extratosKeys = {
  all: ['extratos'] as const,
  list: (userId: string, filters?: Record<string, unknown>) =>
    [...extratosKeys.all, userId, filters] as const,
};

// ========================================
// APORTES
// ========================================
export const aportesKeys = {
  all: ['aportes'] as const,
  lists: () => [...aportesKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...aportesKeys.lists(), filters] as const,
  details: () => [...aportesKeys.all, 'detail'] as const,
  detail: (aporteId: string) =>
    [...aportesKeys.details(), aporteId] as const,
  byInvestment: (investmentId: string) =>
    [...aportesKeys.all, 'by-investment', investmentId] as const,
  byUser: (userId: string) =>
    [...aportesKeys.all, 'by-user', userId] as const,
};

// ========================================
// USERS & PROFILES
// ========================================
export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...usersKeys.lists(), filters] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (userId: string) =>
    [...usersKeys.details(), userId] as const,
  profile: (userId: string) =>
    [...usersKeys.detail(userId), 'profile'] as const,
};

// ========================================
// DOCUMENTS
// ========================================
export const documentsKeys = {
  all: ['documents'] as const,
  lists: () => [...documentsKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...documentsKeys.lists(), filters] as const,
  byInvestment: (investmentId: string) =>
    [...documentsKeys.all, 'by-investment', investmentId] as const,
  byAporte: (aporteId: string) =>
    [...documentsKeys.all, 'by-aporte', aporteId] as const,
};

// ========================================
// ANNOUNCEMENTS & NOTIFICATIONS
// ========================================
export const announcementsKeys = {
  all: ['announcements'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...announcementsKeys.all, 'list', filters] as const,
  detail: (announcementId: string) =>
    [...announcementsKeys.all, announcementId] as const,
};

export const notificationsKeys = {
  all: ['notifications'] as const,
  list: (userId: string) =>
    [...notificationsKeys.all, userId] as const,
  unreadCount: (userId: string) =>
    [...notificationsKeys.list(userId), 'unread-count'] as const,
};

// ========================================
// SCP DISTRIBUTIONS (Private/SCP)
// ========================================
export const scpDistributionsKeys = {
  all: ['scp-distributions'] as const,
  lists: () => [...scpDistributionsKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...scpDistributionsKeys.lists(), filters] as const,
  byInvestment: (investmentId: string) =>
    [...scpDistributionsKeys.all, 'by-investment', investmentId] as const,
  records: (distributionId: string) =>
    [...scpDistributionsKeys.all, 'records', distributionId] as const,
};

// ========================================
// INCC Updates
// ========================================
export const inccKeys = {
  all: ['incc'] as const,
  list: () => [...inccKeys.all, 'list'] as const,
  byMonth: (monthYear: string) =>
    [...inccKeys.all, monthYear] as const,
};

// ========================================
// INVESTMENT MOVEMENTS
// ========================================
export const investmentMovementsKeys = {
  all: ['investment-movements'] as const,
  list: (
    investmentId: string,
    filterType?: string,
    sortOrder?: string,
    userId?: string | null,
    scope?: string
  ) =>
    [
      ...investmentMovementsKeys.all,
      investmentId,
      filterType,
      sortOrder,
      userId,
      scope,
    ] as const,
  byInvestment: (investmentId: string) =>
    [...investmentMovementsKeys.all, investmentId] as const,
};

// ========================================
// INVESTMENT DETAILS (sub-query key used in invalidations)
// ========================================
export const investmentDetailsKeys = {
  all: ['investment-details'] as const,
  detail: (investmentId: string) =>
    [...investmentDetailsKeys.all, investmentId] as const,
};

// ========================================
// INVESTMENT PERFORMANCE
// ========================================
export const investmentPerformanceKeys = {
  all: ['investment-performance'] as const,
  detail: (investmentId: string, userId?: string | null) =>
    [...investmentPerformanceKeys.all, investmentId, userId] as const,
  aggregate: (investmentId: string) =>
    ['investment-performance-aggregate', investmentId] as const,
};

// ========================================
// INVESTMENT DOCUMENTS (Admin)
// ========================================
export const investmentDocumentsAdminKeys = {
  all: ['investment-documents-admin'] as const,
};

// ========================================
// KYC
// ========================================
export const kycKeys = {
  status: (userId: string | undefined) => ['kyc-status', userId] as const,
  statusAll: () => ['kyc-status'] as const,
  documents: (userId: string | undefined) => ['kyc-documents', userId] as const,
  documentsAll: () => ['kyc-documents'] as const,
  adminPending: () => ['admin-kyc-pending'] as const,
};

// ========================================
// COMPLIANCE
// ========================================
export const complianceKeys = {
  metrics: () => ['compliance-metrics'] as const,
  adminInvestorCompliance: (params?: Record<string, unknown>) =>
    ['admin-investor-compliance', params] as const,
};

// ========================================
// ADMIN NOTIFICATIONS
// ========================================
export const adminNotificationsKeys = {
  all: ['admin-notifications'] as const,
  dispatches: (limit?: number) =>
    ['admin-notifications', 'dispatches', limit] as const,
  dispatchesAll: () => ['admin-notifications', 'dispatches'] as const,
  rateLimits: (userId?: string | null) =>
    ['admin-notifications', 'rate-limits', userId] as const,
  rateLimitsAll: () => ['admin-notifications', 'rate-limits'] as const,
  userSearch: (search: string) =>
    ['admin-notifications', 'user-search', search] as const,
};

// ========================================
// DECLARATIONS (CVM-88)
// ========================================
export const declarationsKeys = {
  all: ['declaration-status'] as const,
  status: (userId: string | undefined, version?: string) =>
    [...declarationsKeys.all, userId, version] as const,
  byUser: (userId: string | undefined) =>
    [...declarationsKeys.all, userId] as const,
};

// ========================================
// SUITABILITY
// ========================================
export const suitabilityKeys = {
  validation: (userId: string | undefined) =>
    ['suitability-validation', userId] as const,
};

// ========================================
// TERMS
// ========================================
export const termsKeys = {
  documents: () => ['terms-documents'] as const,
  currentDocument: () => ['terms-documents', 'current'] as const,
  documentsList: () => ['terms-documents', 'list'] as const,
  acceptance: (userId: string | undefined, version?: string) =>
    ['terms-acceptance', userId, version] as const,
  adminAcceptances: (filters?: unknown) =>
    ['admin-terms-acceptances', filters] as const,
};

// ========================================
// APORTE METRICS
// ========================================
export const aporteMetricsKeys = {
  byAportes: (aporteIdsKey: string) =>
    ['aporte-metrics', aporteIdsKey] as const,
};

// ========================================
// OPORTUNIDADES
// ========================================
export const oportunidadesKeys = {
  all: ['oportunidades'] as const,
};

// ========================================
// EVOLUCAO MENSAL
// ========================================
export const evolucaoMensalKeys = {
  byUser: (userId: string | undefined) =>
    ['evolucao-mensal', userId] as const,
};

// ========================================
// PATRIMÔNIO EVOLUTION
// ========================================
export const patrimonioEvolutionKeys = {
  byUser: (userId: string | undefined) =>
    ['patrimonio-evolution', userId] as const,
};

// ========================================
// PRIVATE METRICS (SCP)
// ========================================
export const privateMetricsKeys = {
  byInvestment: (investmentId: string) =>
    ['private-metrics', investmentId] as const,
};

// ========================================
// ROI PAYBACK
// ========================================
export const roiPaybackKeys = {
  detail: (investmentId: string, valorAportado: number, userId?: string | null) =>
    ['roi-payback', investmentId, valorAportado, userId] as const,
  aggregate: (investmentId: string, valorAportado: number) =>
    ['roi-payback-aggregate', investmentId, valorAportado] as const,
};

// ========================================
// MFA (Master Franqueado A)
// ========================================
export const mfaKeys = {
  carteira: (userId: string | undefined) =>
    ['mfa-carteira', userId] as const,
  dashboard: (userId: string | undefined) =>
    ['mfa-dashboard', userId] as const,
  investmentDetails: (investmentId: string, userId: string | undefined) =>
    ['mfa-investment-details', investmentId, userId] as const,
};

// ========================================
// MFB (Master Franqueado B)
// ========================================
export const mfbKeys = {
  carteira: (userId: string | undefined) =>
    ['mfb-carteira', userId] as const,
  carteiraById: (mfbId: string) =>
    ['mfb-carteira-by-id', mfbId] as const,
  dashboard: (userId: string | undefined) =>
    ['mfb-dashboard', userId] as const,
  investmentDetails: (investmentId: string, userId: string | undefined) =>
    ['mfb-investment-details', investmentId, userId] as const,
  privateInvestmentDetails: (id: string, userId: string | undefined) =>
    ['mfb-private-investment-details', id, userId] as const,
};

// ========================================
// PARTNER (Escritório)
// ========================================
export const partnerKeys = {
  carteira: (userId: string | undefined) =>
    ['partner-carteira', userId] as const,
  dashboard: (userId: string | undefined) =>
    ['partner-dashboard', userId] as const,
  documents: (userId: string | undefined, selectedInvestorId?: string | null) =>
    ['partner-documents', userId, selectedInvestorId] as const,
};

// ========================================
// ASSESSOR
// ========================================
export const assessorKeys = {
  documents: (userId: string | undefined, selectedInvestorId?: string | null) =>
    ['assessor-documents', userId, selectedInvestorId] as const,
  investmentData: (assessorId: string, investmentId: string) =>
    ['assessor-investment-data', assessorId, investmentId] as const,
};

// ========================================
// ESCRITÓRIO (Partner/Escritório role)
// ========================================
export const escritorioKeys = {
  dashboard: (userId: string | undefined) =>
    ['escritorio-dashboard', userId] as const,
  carteiraById: (escritorioId: string) =>
    ['escritorio-carteira-by-id', escritorioId] as const,
};

// ========================================
// INVESTOR DOCUMENTS
// ========================================
export const investorDocumentsKeys = {
  byInvestor: (investorId: string | undefined) =>
    ['investor-documents', investorId] as const,
};
